/**
 * Created by Ivo Mehide <ivo@degeetia.ee> on 06.10.17.
 */

import _ from "underscore";
import {NotFoundException} from "./Exceptions";
import SchemaRegistry from "./services/SchemaRegistry";

/**
 * @typedef {Object} LocationProps
 * @property {String} path Full path (including base path), always starts with "/" and does not contain "/" at the end (except for root path "/")
 * @property {String} fieldPath Nav Schema field path
 * @property {?String} resource Resource type name, if exists
 * @property {?String} action Action name, one of: "list", "view", "new", "edit", "delete" or null (if resource type name is missing)
 * @property {?String} id Resource id, only peresent in case of "view", "edit", "delete" actions
 * @property {?String} tab Page tab
 * @property {?Object} query Query parameters, only present in last LocationProps element in location stack
 * @property {?String} parentPath Parent resource full path (including base path), always starts with "/" and does not contain "/" at the end (except for root path "/")
 * @property {?String} parentFieldPath Parent resorce Nav Schema path
 * @property {?String} parentResource Parent resource type name (if parent resource exists)
 * @property {?String} parentAction Parent resource action (if parent resource exists), one of: "list", "view", "new", "edit", "delete"
 * @property {?String} parentId Parent resource ID (if parent resource exists), only peresent in case of "view", "edit", "delete" parent resource actions
 * @property {?String} parentField Parent resource field, if exists
 * @property {?String} parentTab Parent page tab
 */

/**
 * Klass parsib konstruktoris antud path info baasilt lahti parameetrid.
 *
 Realiseeritud on järgmine URLide struktuur:

 Path                                                                           | Kirjeldus
 -------------------------------------------------------------------------------|----------
 /                                                                              | Avavaade
 /`resource`                                                                    | Vastavat tüüpi ressurside nimekirja vaade. Kuvatakse otsinguvorm ning selle all resssursside navigeeritav nimekiri.
 /`resource`?`query`                                                            | Vastavat tüüpi ressurside filtreeritud nimekirja vaade. Kuvatakse filtri tingimustega täidetud otsinguvorm ning selle all filtri tingimustele vastavate resssursside navigeeritav nimekiri.
 /`resource`/`action`[#tab]                                                     | Ressursinimekirjaga seotud tegevuse vaade (näiteks "new" korral ressursi lisamine)
 /`resource`/`action`[#tab]/`subresource`                                       | Ressursinimekirjaga seotud tegevuse vaate alamressursside nimekirja vaade
 /`resource`/`action`[#tab]/`subresource`?`query`                               | Ressursinimekirjaga seotud tegevuse vaate alamressursside filtreeritud nimekirja vaade
 /`resource`/`action`[#tab]/`subresource`/`subaction`[#tab]                     | Ressursinimekirjaga seotud tegevuse vaate alamobjektinimekirjaga seotud tegevuse modal vaade (näiteks "new" korral selle lisamine)
 /`resource`/`action`[#tab]/`subresource`/`subresourceId`/`subaction`[#tab]     | Ressursinimekirjaga seotud tegevuse vaate alamobjekti muutmise modal vaade
 /`resource`/`id`[#tab]                                                         | Ressursi vaatamise vaade
 /`resource`/`id`[#tab]/`action`                                                | Ressursi spetsialiseeritud tegevus
 /`resource`/`id`[#tab]/`subresource`                                           | Ressursi alamressursside nimekirja modal vaade
 /`resource`/`id`[#tab]/`subresource`?`query`                                   | Ressursi alamressursside filtreeritud nimekirja modal vaade
 /`resource`/`id`[#tab]/`subresource`/`action`[#tab]                            | Ressursile alamressursi lisamise modal vaade
 /`resource`/`id`[#tab]/`subresource`/`subresourceId`/`action`[#tab]            | Ressursi alamressursi muutmise modal vaade
 /`resource`/`id`/`action`[#tab]                                                | Ressursi muutmise vaade
 /`resource`/`id`/`action`[#tab]/`modalaction`                                  | Ressursiga seotud tegevuse modal vaade, mis on avatud mingi ressursiga seotud tegevuse vaate peale
 /`resource`/`id`/`action`[#tab]/`subresource`                                  | Ressursi alamressursside nimekirja modal vaade
 /`resource`/`id`/`action`[#tab]/`subresource`?`query`                          | Ressursi alamressursside filtreeritud nimekirja modal vaade
 /`resource`/`id`/`action`[#tab]/`subresource`/`action`[#tab]                   | Ressursile alamressursi lisamise modal vaade
 /`resource`/`id`/`action`[#tab]/`subresource`/`subresourceId`/`action`[#tab]   | Ressursi alamressursi muutmise modal vaade

 URLis saab kasutada eraldajana "/" asemel ka ":" - see tähendab, et antud positsiooni peale eraldi Breadcrumb taset
 ei tehta (st sellise positsiooni peale pole võimalik navigeerida).

 URL komponentide korral kohtades, kus URLi enda struktuurist pole üheselt võimalik aru saada, mis komponendiga on tegu,
 vaadatakse see järgi ressursi Nav schema baasilt järgmiselt:

 * Kõige kõrgema taseme komponent määratleb alati ressursi ning sellele seatakse vastavusse tegevus "list". Lahenduses
   peab olema registreeritud vastava nimega Nav Schema.
 * Kui Nav schema antud komponenti vastavas kohas ei sisalda, kuid sisaldab elementi "*", loetakse see komponent olema
   kas siis ressursi või alamressursi ID ning tegevuseks "view".
 * Kui Nav schema sisaldab vastavas kohas elementi ning selle all on esitatud "ui:resource" väli, siis eeldatakse, et
   tegemist on ressursi määranguga, ressursiks saab olema toodud "ui:resource" välja väärtus ning tegevuseks komponendi
   nimi. Juhul, kui varasem ressurss omab antud komponendi nimega kokkulangevat alamvälja, siis saab tegevuse nimeks
   "list" ning väärtustatakse "parentField" parameeter antud komponendi nimega.
 * Kui Nav schema sisaldab vastavas kohas elementi ning selle all ei ole esitatud "ui:resource" välja, siis eeldatakse,
   et tegemist on tegevuse määranguga.
 * Kõikidel muudel juhtudel eeldatakse, et tegemist on vigase URLiga ning genereeritakse Not Found (404) staatusega vastus.

 Põhiväljade määrangud kanduvad allapoole edasi järgmiselt:
 * Ressursi määrang kandub edasi alamatele tasemetele, kuni mingil tasemel tuleb "ui:resource" abil uus määrang.
 * Tegevuse määrang otsustatakse igal tasemel uuesti vatavalt eelpool kirjeldatule.
 * ID määrang kandub edasi alamatele tasemetele niikaua, kuni mingil tasemel tuleb uue ressursi määrang.
 * Tab määrang edasi ei kandu

 Parent väljad väärtustatakse juhul, kui on vaja põhiväljade komplektiga määratud sisu esitada modal aknas. Sellisel
 juhul kajastavad parent väljad ehele vastavat sisu ja põhiväljad modal akna sisu. Parent väljad väärtustatakse juhul,
 kui Nav Schemas esitatakse elemendi "ui:page" väärtuseks "Modal". Sealjuures tuvastatakse, et kas antud taseme nimi
 on vanemressursi alamvälja nimi. Kui see on nii, siis toimub ka automaatne ressursi vahetus (analoogselt nagu oleks
 Nav Schemas antud kohas olnud esitatud "ui:resource" element).

 URLi struktuuri baasilt tekitatakse kontroller komponendile parameetrid

 Parameeter         | Tähendus
 -------------------|--------------
 path               | Täispikk URLi path osa
 fieldPath          | Nav Schema väljamäärangu string, mis määratleb antud path'ile vastava Nav Schema osa.
 resource           | Ressursi nimi. Alamressursi korral on nimi tuletatud ressursi välja baasilt (st - pole mitte ressursi väli, vaid sellele väljale vastav ressurss)
 action             | Ressursiga seotud tegevus, üks järgmistest: `list`, `new`, `edit`, `delete`, `view` või siis spetsiifiline
 id                 | Ressursi ID (tegevuste `edit`, `delete`, `view` korral)
 query              | Päringu query
 tab                | Lehe tab
 parentPath         | Täispikk URLi path osa, mis vastab vanemressursile
 parentFieldPath    | Vanemressursi Nav Schema väljamäärangu string, mis määratleb antud path'ile vastava Nav Schema osa.
 parentResource     | Vanemressursi nimi, kui tegemist on alamressurssiga
 parentAction       | Vanemressursiga seotud tegevus, kui vanemressurss on määratud. Üks järgmistest: `new`, `edit`, `view`
 parentId           | Vanemressursi ID, kui vanemressurss on määratud ja vanamressursiga seotud tegevus on `edit` või `view`
 parentField        | Vanemressursi välja nimi, mille alamressurssiga tegemist on
 parentTab          | Vanemlehe tab

 Alamressursside korral leitakse ressursi nimi vanemressursi vastava välja UI Schema elemendi `ui:resource` väärtuse abil.
 Kui see on määramata, eeldatakse, et alamressursi nimi langeb vanemressursi välja nimega kokku.

 Ressursi alamtegevuse korral on väärtustatud ka vanemressursi väljad, mis vastavad sellisel juhul selle sama ressursi vanemtegevusele.

 */
class LocationInfo {
    /**
     *
     * @param {String} basePath UI base path (starting with "/")
     * @param {?String} path Full path from the URL (including basePath) without the query string, but with the hash
     * @param {?Object} query Parsed query from the query string
     */
    constructor(basePath, path, query) {
        /**
         * @type {String}
         */
        this.basePath = basePath;
        /**
         *
         * @type {LocationProps[]}
         */
        this.locationStack = this.parseLocationStack(basePath, this.getPathInfo(basePath, path), query);
    }

    /**
     *
     * @param {LocationProps} item
     * @param {String} baseUrl
     * @returns {string}
     */
    static makeResourceUrl(item, baseUrl = null) {
        let path = [];
        if (item.parentResource) {
            path.push(item.parentResource);
            if (_.has(item, "parentId")) path.push(item.parentId);
            switch (item.parentAction) {
                case "list":
                case "view":
                    break;
                default:
                    path.push(item.parentAction);
            }
            if (item.parentTab) path[path.length-1] += "#" + item.parentTab;
        }

        if (item.resource) {
            if (item.parentField) path.push(item.parentField);
            else if (!item.parentResource) path.push(item.resource);
            if (_.has(item, "id")) path.push(item.id);
            switch (item.action) {
                case "list":
                case "view":
                    break;
                default:
                    path.push(item.action);
            }
            if (item.tab) path[path.length-1] += "#" + item.tab;
        }
        return (baseUrl ? baseUrl : "") + "/" + path.join("/");
    }

    clone() {
        let newLocationInfo = new LocationInfo(this.basePath);
        newLocationInfo.locationStack = this.locationStack.slice();
        return newLocationInfo;
    }

    /**
     * Returns base path of UI
     * @returns {String} Base path starting with '/' and without ending slash
     */
    getBasePath() {
        return this.basePath;
    }

    /**
     * Returns location stack for the current URL
     *
     * @returns {LocationProps[]}
     */
    getLocationStack() {
        return this.locationStack;
    }

    /**
     * Returns resoruce name used for navigation schema
     * @returns {string}
     */
    getNavResource() {
        if (this.locationStack == null || this.locationStack.length < 2) return null;
        return this.locationStack[1].resource;
    }
    /**
     * Returns current location props
     * @param {Number} offset Offset - positive numbers select item from location stack start, negative from end (-1 means last item). By default offset is -1.
     * @returns {LocationProps}
     */
    getLocationProps(offset = -1) {
        if (offset == null) offset = this.locationStack.length-1;
        if (offset < 0) offset = this.locationStack.length + offset;
        if (offset < 0 || offset >= this.locationStack.length) return null;
        return this.locationStack[offset];
    }

    /**
     * Returns parent location props
     * @param {Number} offset Offset - positive numbers select item from location stack start, negative from end (-1 means last item). By default offset is -1.
     * @returns {LocationProps}
     */
    getParentLocationProps(offset = -1) {
        if (offset == null) offset = this.locationStack.length-1;
        if (offset < 0) offset = this.locationStack.length + offset;
        if (offset < 0 || offset >= this.locationStack.length) return null;
        let props = this.locationStack[offset];
        if (!props.parentResource) return null;
        /**
         * @type {LocationProps}
         */
        let parentProps = {
            resource: props.parentResource
        };
        if (props.parentPath) parentProps.path = props.parentPath;
        if (props.parentFieldPath) parentProps.fieldPath = props.parentFieldPath;
        if (props.parentAction) parentProps.action = props.parentAction;
        if (_.has(props, "parentId")) parentProps.id = props.parentId;
        if (props.parentTab) parentProps.tab = props.parentTab;
        return parentProps;
    }

    /**
     * Returns JSON Shema field path for Navigation stack item
     * @param item
     * @returns {string}
     */
/*    getJsonSchemaFieldPath(item) {
        if (!item) {
            if (this.locationStack.length == 0) return null;
            item = this.locationStack[this.locationStack.length-1];
        }
        let fieldPath = [];
        if (item.parentResource) {
            if (item.parentId) fieldPath.push("[" + item.parentId + "]");
        }
        if (item.resource) {
            if (item.parentField) fieldPath.push(item.parentField);
            else if (item.parentResource) fieldPath.push(item.resource);
            if (item.id) fieldPath.push("[" + item.id + "]");
        }
        return fieldPath.join("/");
    }*/

    /**
     * @protected
     * @param {String} basePath
     * @param {String} path
     * @returns {String}
     */
    getPathInfo(basePath, path) {
        if (!path) return null;
        let remove = path.indexOf("?");
        if (remove >= 0) {
            path = path.substring(0, remove);
        }

        if (!basePath || basePath === "/") return path;
        if (path.startsWith(basePath)) return path.substring(basePath.length);
        // Invalid path:
        throw new NotFoundException();
    }

    /**
     * Based by current props and field name detect subfield props
     * @param {LocationProps} props
     * @param {string} fieldName
     * @returns {LocationProps}
     */
    makeSubfieldProps(props, fieldName) {
        let schema = SchemaRegistry.getNavigationSchemaObject(props.resource);
        return {
            parentResource: props.resource,
            parentField: fieldName,
            parentFieldPath: props.fieldPath,
            parentAction: props.action,
            parentId: props.id,
            parentPath: props.path,
            parentTab: props.tab,
            resource: schema.field(props.fieldPath + "/" + fieldName).getResource()
        };
    }



    /**
     * Makes field path (relative to resource) based by location props.
     * @param {LocationProps} item
     * @returns {string}
     */
    makeResourceFieldPath(item) {
        let path = [];
        if (item.parentResource) {
            if (_.has(item, "parentId")) path.push("*");
            switch (item.parentAction) {
                case "list":
                case "view":
                    break;
                default:
                    path.push(item.parentAction);
            }
            if (item.parentTab && path.length > 0) path[path.length-1] += "#" + item.parentTab;
        }

        if (item.resource) {
            if (item.parentField) path.push(item.parentField);
            if (_.has(item, "id")) path.push("*");
            switch (item.action) {
                case "list":
                case "view":
                    break;
                default:
                    path.push(item.action);
            }
            if (item.tab && path.length > 0) path[path.length-1] += "#" + item.tab;
        }
        return path.join("/");
    }

    /**
     * Return resource field path
     * @param {Number} offset Offset - positive numbers select item from location stack start, negative from end (-1 means last item). By default offset is -1.
     * @returns {String}
     */
    getResourceFieldPath(offset = -1) {
        if (offset == null) offset = this.locationStack.length-1;
        if (offset < 0) offset = this.locationStack.length + offset;
        if (offset < 0 || offset >= this.locationStack.length) return null;
        return this.locationStack[offset].fieldPath;
    }

    /**
     * Returns parent resource field path for the specified navigation stack item. If item is not specified than last item from the
     * stack will be used.
     * @param {Number} offset Offset - positive numbers select item from location stack start, negative from end (-1 means last item). By default offset is -1.
     * @returns {String} Field path for use in Schema (without starting slash), resource type name is removed from start
     */
    getParentResourceFieldPath(offset = -1) {
        if (offset == null) offset = this.locationStack.length-1;
        if (offset < 0) offset = this.locationStack.length + offset;
        if (offset < 0 || offset >= this.locationStack.length) return null;
        return this.locationStack[offset].parentFieldPath;
    }

    /**
     *
     * @param {?Number} offset
     * @returns {?String}
     */
    getResourceFieldPathWithoutTab(offset = -1) {
        let path = this.getResourceFieldPath(offset);
        if (!path) return path;
        let idx = path.lastIndexOf("#");
        let idx2 = path.lastIndexOf("/");
        if (idx >= 0 && idx > idx2) {
            return path.substring(0, idx-1);
        }
        return path;
    }

    /**
     *
     * @param {?Number} offset
     * @returns {?String}
     */
    getParentResourceFieldPathWithoutTab(offset = -1) {
        let path = this.getParentResourceFieldPath(offset);
        if (!path) return path;
        let idx = path.lastIndexOf("#");
        let idx2 = path.lastIndexOf("/");
        if (idx >= 0 && idx > idx2) {
            return path.substring(0, idx-1);
        }
        return path;
    }

    /**
     * @protected
     * @param {?String} path Full path (that is: basePath + "/" + resource + paramPath)
     * @param {?String} pathInfo Path after the base path
     * @param {?Object} query Päringuparameetrite objekt
     * @returns {LocationProps[]}
     */
    parseLocationStack(basePath, pathInfo, query) {
        if (!pathInfo) return [];

        if (!basePath) basePath = "";
        else {
            if (!basePath.startsWith("/")) basePath = "/" + basePath;
            if (basePath.endsWith("/")) basePath = basePath.substring(0, basePath.length-1);
        }
        /**
         * @type {LocationProps[]}
         */
        let stack = [{
            path: (basePath === "" ? "/" : basePath),
            action: null
        }];

        if (!pathInfo) pathInfo = "";
        if (pathInfo.startsWith("/")) pathInfo = pathInfo.substring(1);
        while (pathInfo.endsWith("/")) pathInfo = pathInfo.substring(0, pathInfo.length-1);

        let pathInfoItems = (pathInfo === "" ? [] : pathInfo.split("/"));
        /**
         * @type {LocationProps}
         */
        let lastItem = stack[0];
        let path = basePath;
        if (path === "/") path = "";

        let navSchema = null;

        for (let p of pathInfoItems) {
            path += "/" + p;
            /**
             * @type {LocationProps}
             */
            let newItem = {};
            if (p.indexOf(":") >= 0) {
                let subitems = p.split(":");
                let newItemTemp = lastItem;
                let firstNewItem = null;
                for (let s of subitems) {
                    newItemTemp = this.getNewItem(navSchema, newItemTemp, path, decodeURIComponent(s));
                    if (navSchema == null) {
                        navSchema = SchemaRegistry.getNavigationSchemaObject(s);
                    }
                    if (!firstNewItem) firstNewItem = newItemTemp;
                }
                newItem = newItemTemp;
                if (firstNewItem) {
                    // Assign parent fields from first new item:
                    for (let f of ["parentResource", "parentField", "parentAction", "parentId", "parentTab", "parentPath"]) {
                        if (firstNewItem[f]) newItem[f] = firstNewItem[f];
                        else if (newItem[f]) delete newItem[f];
                    }
                }
            } else {
                newItem = this.getNewItem(navSchema, lastItem, path, decodeURIComponent(p));
                if (navSchema == null) {
                    navSchema = SchemaRegistry.getNavigationSchemaObject(p);
                }
            }
            if (navSchema.isEmpty()) throw new NotFoundException();
            stack.push(newItem);
            lastItem = newItem;
        }
        if (query) lastItem.query = query;
        return stack;
    }

    _getRelative(p) {
        if (p != null && p.startsWith("/")) return p.substring(1);
        return p;
    }

    /**
     *
     * @protected
     * @param {UiSchema} navSchema
     * @param {LocationProps} lastItem
     * @param {String} path
     * @param {String} p
     * @returns {LocationProps}
     */
    getNewItem(navSchema, lastItem, path, p) {
        /**
         * @type {LocationProps}
         */
        let newItem = {
            path: path
        };

        let hash = p.indexOf("#");
        if (hash >= 0) {
            newItem.tab = p.substring(hash+1);
            p = p.substring(0, hash);
        }

        if (navSchema == null) {
            // This is first element in URL. Corresponds to the resource listing:
            newItem.fieldPath = "";
            if (newItem.tab) newItem.fieldPath = "#" + newItem.tab;
            newItem.resource = p;
            newItem.action = "list";
            return newItem;
        }

        let field = navSchema.field(lastItem.fieldPath);
        newItem.fieldPath = lastItem.fieldPath;

        if (field.hasField(p)) {
            // Action field
            newItem.fieldPath = this._getRelative(newItem.fieldPath + "/" + p);
            newItem.action = p;
            field = field.field(p);
        } else if (field.hasField("*")) {
            // ID field
            newItem.fieldPath = this._getRelative(newItem.fieldPath + "/*");
            newItem.action = "view";
            newItem.id = p;
            field = field.field("*");
        } else {
            throw new NotFoundException();
        }

        if (newItem.tab) newItem.fieldPath = this._getRelative(newItem.fieldPath + "/#" + newItem.tab);

        // Test if we have resource change:
        let resource = field.getResource();
        if (resource) {
            // Resource switch to the new resource:
            newItem.resource = resource;

            // If we are currently subfield of the parent field, then set parent properties:
            // Check if current resource is actually subfield of parent field:
            if (lastItem.resource && lastItem.resource !== newItem.resource) {
                let parentResourceSchema = SchemaRegistry.getJsonSchema(lastItem.resource);
                if (parentResourceSchema &&
                    parentResourceSchema.properties &&
                    parentResourceSchema.properties[p]) {
                    newItem.parentResource = lastItem.resource;
                    newItem.parentField = p;
                    newItem.action = "list";

                    newItem.parentAction = lastItem.action;
                    if (_.has(lastItem, "id")) newItem.parentId = lastItem.id;
                    if (lastItem.tab) newItem.parentTab = lastItem.tab;
                    newItem.parentFieldPath = lastItem.fieldPath;
                    newItem.parentPath = lastItem.path;
                }

                // Save prev info:
                if (lastItem.resource) newItem.prevResource = lastItem.resource;
                if (lastItem.action) newItem.prevAction = lastItem.action;
                if (_.has(lastItem, "id")) newItem.prevId = lastItem.id;
                if (lastItem.tab) newItem.prevTab = lastItem.tab;
                if (lastItem.fieldPath) newItem.prevFieldPath = lastItem.fieldPath;
                if (lastItem.path) newItem.prevPath = lastItem.path;
            }

        } else {
            // Continuing with the same resource:
            if (!newItem.resource) newItem.resource = lastItem.resource;
            if (!_.has(newItem, "id") && _.has(lastItem, "id")) newItem.id = lastItem.id;
        }

        if (!newItem.parentResource && lastItem.parentResource) {
            // If we already have parent field specifictaion then continue with this:
            for (let f of ["parentPath", "parentFieldPath", "parentResource", "parentAction", "parentId", "parentTab", "parentField"]) {
                if (_.has(lastItem, f)) newItem[f] = lastItem[f];
            }
        }

        // Keep prev info:
        if (!newItem.prevResource && lastItem.prevResource) {
            // Save prev info:
            if (lastItem.prevResource) newItem.prevResource = lastItem.prevResource;
            if (lastItem.prevAction) newItem.prevAction = lastItem.prevAction;
            if (_.has(lastItem, "prevId")) newItem.prevId = lastItem.prevId;
            if (lastItem.prevTab) newItem.prevTab = lastItem.prevTab;
            if (lastItem.prevFieldPath) newItem.prevFieldPath = lastItem.prevFieldPath;
            if (lastItem.prevPath) newItem.prevPath = lastItem.prevPath;
        }

        // Set parent resource fields if there is specified to use Modal window:
        let page = field.getPage();
        if (page === "Modal") {
            if (!newItem.parentResource) {
                newItem.parentResource = lastItem.resource;
                newItem.parentAction = lastItem.action;
                if (_.has(lastItem, "id")) newItem.parentId = lastItem.id;
                if (lastItem.tab) newItem.parentTab = lastItem.tab;
                newItem.parentFieldPath = lastItem.fieldPath;
                newItem.parentPath = lastItem.path;
            }
        }

        return newItem;
    }

}

export default LocationInfo;
